import { Check, Loader2, Search, X } from "lucide-react"
import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { searchIntentions } from "@/features/intentions/services/intention.service"
import { cn } from "@/lib"

interface IntentionItem {
  id: string
  name: string
}

interface IntentionsInputProps {
  id: string
  label: string
  placeholder?: string
  selectedIntentions: IntentionItem[]
  onAdd: (intention: IntentionItem) => void
  onRemove: (id: string) => void
  error?: string
  onClearError?: () => void
  disabled?: boolean
  required?: boolean
}

const PAGE_SIZE = 20

function IntentionsSearchDropdown({
  inputValue,
  onSelect,
  onClose,
  inputRef,
  excludeIds,
  containerRef,
}: {
  inputValue: string
  onSelect: (intention: IntentionItem) => void
  onClose: () => void
  inputRef: RefObject<HTMLInputElement | null>
  excludeIds: Set<string>
  containerRef: RefObject<HTMLDivElement | null>
}) {
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const [intentions, setIntentions] = useState<IntentionItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const listRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<(HTMLLIElement | null)[]>([])
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Fetch intentions (first page or all pages for a search)
  const fetchIntentions = useCallback(
    async (query: string, page: number, append: boolean = false) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }

      try {
        const result = await searchIntentions(query, page, PAGE_SIZE)
        const filtered = result.intentions
          .filter(i => !excludeIds.has(i.id))
          .map(i => ({ id: i.id, name: i.name }))

        setIntentions(prev => (append ? [...prev, ...filtered] : filtered))
        setTotalPages(result.totalPages)
        setCurrentPage(page)
        setHasSearched(true)
      } catch {
        if (!append) {
          setIntentions([])
        }
        setHasSearched(true)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [excludeIds],
  )

  // On mount — load all intentions (no search query)
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchIntentions is stable (useCallback)
  useEffect(() => {
    fetchIntentions("", 1)
  }, [])

  // On search query change — debounce and fetch from page 1
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!inputValue.trim()) {
      // Cleared search — reload all intentions from page 1
      fetchIntentions("", 1)
      return
    }

    setIsLoading(true)
    debounceRef.current = setTimeout(() => {
      fetchIntentions(inputValue, 1)
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [inputValue, fetchIntentions])

  // Infinite scroll via IntersectionObserver on sentinel element
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry.isIntersecting && !isLoading && !isLoadingMore && currentPage < totalPages) {
          fetchIntentions(inputValue, currentPage + 1, true)
        }
      },
      { rootMargin: "100px" },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [isLoading, isLoadingMore, currentPage, totalPages, inputValue, fetchIntentions])

  useLayoutEffect(() => {
    if (containerRef.current && inputRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const inputRect = inputRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: inputRect.bottom - containerRect.top + 4,
        left: inputRect.left - containerRect.left,
        width: inputRect.width,
      })
    }
  }, [containerRef, inputRef])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setFocusedIndex(prev => (prev < intentions.length - 1 ? prev + 1 : prev))
          break
        case "ArrowUp":
          e.preventDefault()
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1))
          break
        case "Enter":
          e.preventDefault()
          if (focusedIndex >= 0 && intentions[focusedIndex]) {
            onSelect(intentions[focusedIndex])
            setFocusedIndex(-1)
          }
          break
        case "Escape":
          e.preventDefault()
          onClose()
          break
      }
    },
    [focusedIndex, intentions, onSelect, onClose],
  )

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }, [focusedIndex])

  useEffect(() => {
    const input = inputRef.current
    if (input) {
      input.addEventListener("keydown", handleKeyDown as unknown as EventListener)
      return () => {
        input.removeEventListener("keydown", handleKeyDown as unknown as EventListener)
      }
    }
  }, [handleKeyDown, inputRef])

  useEffect(() => {
    setFocusedIndex(-1)
  }, [])

  if (!dropdownPosition) return null

  const showIntentions = intentions.length > 0
  const showEmpty = hasSearched && !isLoading && intentions.length === 0
  const hasMore = currentPage < totalPages

  return (
    <div
      className="absolute z-50 bg-popover border rounded-md shadow-lg overflow-hidden"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        maxHeight: "300px",
      }}
    >
      {isLoading && !showIntentions && (
        <div className="p-4 space-y-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-10 bg-muted rounded animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      )}

      {showIntentions && (
        <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b flex items-center gap-2">
          <Search className="size-3" />
          <span>
            {isLoading ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              `${intentions.length} intention${intentions.length !== 1 ? "s" : ""}`
            )}
          </span>
        </div>
      )}

      {showIntentions && (
        <ul ref={listRef} className="max-h-[240px] overflow-y-auto">
          {intentions.map((intention, index) => (
            <li
              key={intention.id}
              ref={el => {
                itemRefs.current[index] = el
              }}
              className={cn(
                "px-3 py-2 cursor-pointer flex items-center justify-between gap-1 transition-colors",
                focusedIndex === index ? "bg-accent" : "hover:bg-muted/50",
              )}
              onMouseDown={e => {
                e.preventDefault()
                onSelect(intention)
              }}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span className="truncate flex-1 text-[11px]">{intention.name}</span>
              {focusedIndex === index && <Check className="size-4 text-primary shrink-0" />}
            </li>
          ))}

          {/* Sentinel for infinite scroll */}
          {hasMore && <div ref={sentinelRef} className="h-1" />}

          {isLoadingMore && (
            <li className="px-3 py-2 flex justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </li>
          )}
        </ul>
      )}

      {showEmpty && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          No intentions found for &ldquo;{inputValue}&rdquo;
        </div>
      )}
    </div>
  )
}

export function IntentionsInput({
  id,
  label,
  placeholder,
  selectedIntentions,
  onAdd,
  onRemove,
  error,
  onClearError,
  disabled = false,
  required = false,
}: IntentionsInputProps) {
  const [inputValue, setInputValue] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const skipDropdownOpenRef = useRef(false)

  const excludeIds = new Set(selectedIntentions.map(i => i.id))

  const handleFocus = () => {
    if (disabled) return
    if (error && onClearError) onClearError()
    if (skipDropdownOpenRef.current) {
      skipDropdownOpenRef.current = false
      return
    }
    setShowDropdown(true)
  }

  const handleBlur = () => {
    setShowDropdown(false)
    setInputValue("")
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    setInputValue(e.target.value)
    if (error && onClearError) onClearError()
    setShowDropdown(true)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    if (!showDropdown) {
      if (e.key === "Escape") {
        e.preventDefault()
        setInputValue("")
        inputRef.current?.blur()
      }
    }
  }

  const handleDropdownSelect = (intention: IntentionItem) => {
    if (excludeIds.has(intention.id)) return
    onAdd(intention)
    setInputValue("")
    setShowDropdown(false)
    skipDropdownOpenRef.current = true
    inputRef.current?.focus()
  }

  const handleDropdownClose = () => {
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="relative flex flex-col gap-1.25" ref={containerRef}>
      <Label
        htmlFor={id}
        className={cn(
          "text-xs leading-5 uppercase",
          error ? "text-brand-terracotta-bright" : "text-body",
        )}
      >
        {label}
        {required && (
          <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
        )}
      </Label>

      <div
        className={cn(
          "flex gap-2 items-center flex-wrap pb-2 border-b transition-all duration-200",
          error ? "border-brand-terracotta-bright" : "border-input",
        )}
      >
        {selectedIntentions.map(intention => (
          <Badge
            key={intention.id}
            variant="secondary"
            className="gap-1.5 text-[13px] leading-5 p-2.5 pe-2 rounded-md border-white"
          >
            {intention.name}
            <button
              type="button"
              onClick={() => onRemove(intention.id)}
              className="flex items-center justify-center rounded-full cursor-pointer text-white"
              aria-label={`Remove ${intention.name}`}
            >
              <X className="size-4" />
            </button>
          </Badge>
        ))}

        <div className="relative flex-1 min-w-[120px]">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder || "Search intentions..."}
            className="placeholder-text-body-ultra dark:placeholder-text-body-ultra w-full text-sm text-primary focus:outline-none bg-transparent"
            autoComplete="off"
          />
        </div>
      </div>

      {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}

      {showDropdown && (
        <IntentionsSearchDropdown
          inputValue={inputValue}
          onSelect={handleDropdownSelect}
          onClose={handleDropdownClose}
          inputRef={inputRef}
          excludeIds={excludeIds}
          containerRef={containerRef}
        />
      )}
    </div>
  )
}
